import { getHexTileMapKey } from '../../hexmap/model/keys';
import { hexesWithinRadius } from '../../hexmap/model/map';
import type { RuleSet } from '../../rules/schema';
import { buildingMaxHp, type CityBonus, cityBonuses } from '../bonuses';
import { fromMilli, toMilli } from '../fixed';
import { buildingLevel, levelStat } from '../levels';
import { unitsByMint } from '../occupancy';
import { holdsStock } from '../stock';
import { mintOrder } from '../territory';
import type { EntityId, SystemFn, Unit } from '../types';
import { setXp, unitMaxHp, unitRank } from '../veterancy';
import { isStarving } from './upkeep';

/**
 * Healing: every unit with the `heal` action (healers, medics, engineers)
 * mends one own unit a tick. Runs after combat, so this tick's dead stay
 * dead and a unit is judged on the hp it has left. A starving player's
 * healers mend nobody, so healing never cancels upkeep attrition.
 */

/** Whether a unit of this type heals (the `heal` action, with a rate). */
function canHeal(rules: RuleSet, unit: Unit): boolean {
  const def = rules.units[unit.type];
  return !!def && def.actions.includes('heal') && (def.healRate ?? 0) > 0;
}

/** Hp per second `healer` restores: its type's `healRate` plus `strengthPerRank` of it per rank. */
function unitHealRate(rules: RuleSet, healer: Unit): number {
  const rate = rules.units[healer.type]?.healRate ?? 0;
  return rate * (1 + rules.veterancy.strengthPerRank * unitRank(rules, healer));
}

/** Whether `healer` mends units like `patient`: organic healers organic units, and so on. */
function healsKind(rules: RuleSet, healer: Unit, patient: Unit): boolean {
  const heals = rules.units[healer.type]?.heals;
  const mechanical = !!rules.units[patient.type]?.mechanical;
  return heals === (mechanical ? 'mechanical' : 'organic');
}

/**
 * Who `healer` would mend now: among its own units of the kind it heals
 * within its `range`, not itself, not inside a building and short of max
 * hp, the one missing the largest share of its max hp; ties go to the
 * oldest. `at` maps tiles to the units standing on them (outside buildings).
 */
function healTarget(rules: RuleSet, healer: Unit, at: Map<string, Unit[]>): Unit | undefined {
  const range = rules.units[healer.type]?.range ?? 1;
  let best: Unit | undefined;
  let bestMissing = 0;
  for (const cell of hexesWithinRadius(healer.pos, range)) {
    for (const u of at.get(getHexTileMapKey(cell)) ?? []) {
      if (u.id === healer.id || u.owner !== healer.owner || !healsKind(rules, healer, u)) continue;
      const max = unitMaxHp(rules, u);
      const missing = max > 0 ? 1 - u.hp / max : 0;
      if (missing <= 0) continue;
      if (missing > bestMissing || (missing === bestMissing && best && older(u, best))) {
        best = u;
        bestMissing = missing;
      }
    }
  }
  return best;
}

/** Whether `a` was minted before `b`. */
function older(a: Unit, b: Unit): boolean {
  return (
    mintOrder(a.id) - mintOrder(b.id) < 0 || (mintOrder(a.id) === mintOrder(b.id) && a.id < b.id)
  );
}

/**
 * The heal step: each healer outside a building whose owner isn't starving
 * (`isStarving`), oldest first, restores up
 * to its heal rate × dt to its `healTarget`, never past max hp, and earns
 * `veterancy.xpPerHeal` xp per hp restored. Healers act one after another
 * on live hp, so two beside one wounded unit share out the rest of the
 * wounded instead of piling on it once it's the healthiest.
 */
const heal: SystemFn = (state, ctx, _inputs, dt) => {
  const { rules } = ctx;
  const units = unitsByMint(state);
  const healers = units.filter(
    (u) => !u.inside && canHeal(rules, u) && !isStarving(state, rules, u.owner)
  );
  if (healers.length === 0) return;

  const at = new Map<string, Unit[]>();
  for (const u of units) {
    if (u.inside) continue;
    const key = getHexTileMapKey(u.pos);
    const list = at.get(key);
    if (list) list.push(u);
    else at.set(key, [u]);
  }
  const xpPerHeal = rules.veterancy.xpPerHeal ?? 0;
  for (const healer of healers) {
    const patient = healTarget(rules, healer, at);
    if (!patient) continue;
    const amount = Math.min(
      toMilli(unitHealRate(rules, healer) * dt),
      unitMaxHp(rules, patient) - patient.hp
    );
    if (amount <= 0) continue;
    patient.hp += amount;
    if (xpPerHeal > 0) {
      setXp(rules, healer, (healer.xp ?? 0) + fromMilli(amount) * xpPerHeal);
    }
  }
};

/**
 * Repair: a completed building that took damage (`lastDamagedTick`) and
 * hasn't since for `pacing.repairDelay` seconds regains
 * its level's `repair` share, else `pacing.cityRepair` (a city) or
 * `pacing.buildingRepair` (anything else), of its max hp per second, never past it. Back at full hp its
 * `lastDamagedTick` is cleared, so only buildings still mending are looked
 * at (a building never damaged is always at its max: upgrades, bonuses and
 * last stands keep its hp fraction). Runs after combat, so a building hit
 * this tick waits the whole delay again.
 */
const repair: SystemFn = (state, ctx, _inputs, dt) => {
  const { rules } = ctx;
  const { repairDelay, cityRepair, buildingRepair } = rules.pacing;
  const delayTicks = Math.round(repairDelay / dt);
  let bonuses: Map<EntityId, CityBonus> | undefined;
  for (const b of Object.values(state.buildings)) {
    if (b.lastDamagedTick === undefined || b.buildProgress < 1) continue;
    if (state.tick - b.lastDamagedTick < delayTicks) continue;
    bonuses ??= cityBonuses(state, rules);
    const max = buildingMaxHp(state, rules, b, bonuses);
    const def = rules.buildings[b.type];
    const share =
      (def ? levelStat(def, buildingLevel(b), 'repair') : undefined) ??
      (holdsStock(rules, b) ? cityRepair : buildingRepair);
    b.hp = Math.min(max, b.hp + Math.round(max * share * dt));
    if (b.hp >= max) delete b.lastDamagedTick;
  }
};

export { canHeal, heal, healsKind, healTarget, repair, unitHealRate };
